import create from 'zustand'
import {
    Organization, Service, Brand, Body, Place, Garage, PremiumService, User, Slot,
    Key, Gearbox, Generation, Motorization, Equipment, PartnerConfig, NumberPlate
} from '@App/types'
import {PhoneInput} from "@App/components/Booking/PhoneInput";

type Store = {
    filters: {
        serviceFilter: Service
        placeFilter: Place
        vcisFilter?: string[]
        search?: string
        datesFilter: {
            from: Date|null
            to: Date|null
        }
        distanceFilter: number
        premiumServicesFilter?: PremiumService[]
        brandFilter: Brand
        generationFilter: Generation
        motorizationFilter: Motorization
        gearboxFilter: Gearbox
        bodyFilter: Body
        sortBy: string
        currentlyAvailableGenerations: Generation[]
    }
    order: {
        organization: Organization
        service?: Service
        place?: Place
        garage?: Garage
        premiumServices?: PremiumService[]
        user?: User
        slot?: Slot
        brand?: Brand
        generation?: Generation
        motorization?: Motorization
        key?: Key
        body?: Body
        gearbox?: Gearbox
        mileage?: number
        initialRegistrationDate?: Date
        equipments?: Equipment[]
        subRepairs?: string[]
    }
    partner: {
        id: number
        name: string
        slug: string
        shouldDisplayPrize: boolean
        partnerLogo: string
        partnerLogoFirst: boolean
        shouldDisplayQuotation: boolean
        shouldDisplayPhoneIfUnavailable: boolean
        fallbackImage: string
    },
    bookingContact: {
        firstName: string,
        lastName: string,
        mail: string,
        phone: PhoneInput,
        mileage: number,
        street: string,
        city: string,
        postcode: string,
        addition: string,
        numberplate: NumberPlate,
        message: string,
        acceptedTos: boolean,
        acceptedMarketing: boolean,
        acceptedAdacMember: boolean
    },
    garagecount: number|null
    highlightedGarageId: number|string
    isSettingsOpen: boolean
    isVehicleSlectionOpen: boolean
    isLoading: boolean

    setIsSettingsOpen: (isOpen: boolean) => void
    setIsVehicleSlectionOpen: (isOpen: boolean) => void
    setGaragecount: (count: number|null) => void
    setHighlightedGarageId: (id: number|string) => void
    setServiceFilter: (service: Service) => void
    getServiceFilter: () => Service
    setPlaceFilter: (place: Place) => void
    getPlaceFilter: () => Place
    setVcisFilter: (vcis: string[]) => void
    setDatesFilter: (dates: { from: Date|null; to: Date|null }) => void
    setDistanceFilter: (distance: number) => void
    setBrandFilter: (brand: Brand) => void
    setGenerationFilter: (generation: Generation) => void
    setMotorizationfilter: (motirization: Motorization) => void
    setGearboxfilter: (gearbox: Gearbox) => void
    setBodyfilter: (body: Body) => void
    setService: (service: Service) => void
    setPlace: (place: Place) => void
    setSearch: (search: string) => void
    setSortBy: (sortBy: string) => void
    getSortBy: () => string
    setGeneration: (generation: Generation) => void
    setMotorization: (motorization: Motorization) => void
    setBody: (body: Body) => void
    setBrand: (brand: Brand) => void
    setGearbox: (gearbox: Gearbox) => void
    setGarage: (garage: Garage) => void
    setSlot: (slot: Slot) => void
    setIsLoading: (isListLoading: boolean) => void
    setPartnerConfig: (config: PartnerConfig) => void
    setCurrentlyAvailableGenerations: (generations: Generation[]) => void
    setFirstName: (firstName: string) => void
    setLastName: (lastName: string) => void
    setMail: (mail: string) => void
    setPhone: (phone: PhoneInput) => void
    setMileage: (mileage: number) => void
    setStreet: (street: string) => void
    setCity: (city: string) => void
    setPostcode: (postcode: string) => void
    setAddition: (addition: string) => void
    setNumberplate: (numberplate: NumberPlate) => void
    setMessage: (message: string) => void
    setAcceptedTos: (acceptedTos: boolean) => void
    setAcceptedMarketing: (acceptedMarketing: boolean) => void
    setAcceptedAdacMember: (acceptedAdacMember: boolean) => void
    getFirstName: () => string
    getLastName: () => string
    getMail: () => string
    getPhone: () => PhoneInput
    getMileage: () => number
    getStreet: () => string
    getCity: () => string
    getPostcode: () => string
    getAddition: () => string
    getNumberplate: () => NumberPlate
    getMessage: () => string
    getAcceptedTos: () => boolean
    getAcceptedAdacMember: () => boolean
    getAcceptedMarketing: () => boolean
}

const useStore = create<Store>((set, get) => ({
    filters: {
        serviceFilter: {
            id: '2',
            name: 'Termin',
            slug: 'termin',
            icon: 'faScrewdriverWrench',
            pivot: {}
        },
        placeFilter: {
            name: 'Berlin',
            id: '0',
            lat: 52.520008,
            lon: 13.404954,
            address: {
                city: 'Berlin'
            }
        },
        search: '',
        datesFilter: {
            from: null,
            to: null
        },
        distanceFilter: 50000,
        brandFilter: {
            id: '0',
            name: '',
            important: false,
            logo: '',
            icon: ''
        },
        generationFilter: {
            id: 0,
            name: '',
            built_from: new Date(),
        },
        motorizationFilter: {
            id: 0,
            name: '',
        },
        gearboxFilter: {
            id: 0,
            name: '',
            formatted_name: ''
        },
        bodyFilter: {
            id: 0,
            name: '',
        },
        premiumServicesFilter: [],
        sortBy: 'smallest_distance',
        currentlyAvailableGenerations: []
    },
    order: {
        organization: {
            id: 1,
            name: 'repareo'
        },
        service: undefined,
        place: undefined,
        garage: undefined,
        brand: undefined,
        generation: undefined,
        motorization: undefined,
        gearbox: undefined,
        body: undefined,
        slot: undefined
    },
    bookingContact: {
        firstName: '',
        lastName: '',
        mail: '',
        phone: {country: '+49', number: ''},
        mileage: 0,
        street: '',
        city: '',
        postcode: '',
        addition: '',
        numberplate: {region: '', firstID: '', secondID: ''},
        message: '',
        acceptedTos: false,
        acceptedMarketing: false,
        acceptedAdacMember: false
    },
    partner: {
        id: 1,
        name: 'repareo',
        slug: 'werkstatt',
        shouldDisplayPrize: true,
        partnerLogo: 'adacPartnerLogo',
        partnerLogoFirst: false,
        shouldDisplayQuotation: true,
        shouldDisplayPhoneIfUnavailable: false,
        shouldDisplayVehicleSelection: false,
        fallbackImage: 'images/werkstatt-cover-image.png',
        shouldDisplayBrandSelection: true,
        shouldDisplayServiceFilter: true,
        shouldDisplayFooter: true,
        shouldDisplayMenu: true,
        shouldDisplayPhoneHeader: true,
    },
    isSettingsOpen: false,
    isVehicleSlectionOpen: false,
    isLoading: false,
    garagecount: null,
    highlightedGarageId: '',
    setIsSettingsOpen: (isOpen: boolean) => set(state => ({ isSettingsOpen: isOpen })),
    setIsVehicleSlectionOpen: (isOpen: boolean) => set(state => ({ isVehicleSlectionOpen: isOpen })),
    setGaragecount: (garagecount: number|null) => set({ garagecount }),
    setHighlightedGarageId: (garageId: number|string) => set(state => ({ highlightedGarageId: garageId })),
    setServiceFilter: (service: Service) => {
        set(state => ({ filters: { ...state.filters, serviceFilter: service } }))
    },
    getServiceFilter: () => get().filters.serviceFilter,
    setPlaceFilter: (place: Place) => set(state => ({ filters: { ...state.filters, placeFilter: place } })),
    getPlaceFilter: () => get().filters.placeFilter,
    setVcisFilter: (vcis: string[]) => set(state => ({ filters: { ...state.filters, vcisFilter: vcis } })),
    getPartnerConfig: () => get().partner,
    setDatesFilter: (dates: { from: Date|null; to: Date|null }) => set(state => ({ filters: { ...state.filters, datesFilter: dates } })),
    setDistanceFilter: (distance: number) => set(state => ({ filters: { ...state.filters, distanceFilter: distance } })),
    setBrandFilter: (brand: Brand) => set(state => ({ filters: { ...state.filters, brandFilter: brand } })),
    setGenerationFilter: (generation: Generation) => set(state => ({ filters: { ...state.filters, generationFilter: generation } })),
    setMotorizationfilter: (motorization: Motorization) => set(state => ({ filters: { ...state.filters, motorizationFilter: motorization } })),
    setGearboxfilter: (gearbox: Gearbox) => set(state => ({ filters: { ...state.filters, gearboxFilter: gearbox } })),
    setBodyfilter: (body: Body) => set(state => ({ filters: { ...state.filters, bodyFilter: body } })),
    setSearch: (search: string) => set(state => ({ filters: { ...state.filters, search } })),
    setSortBy: (sortBy: string) => set(state => ({ filters: { ...state.filters, sortBy } })),
    getSortBy: () => get().filters.sortBy,
    setService: (service: Service) => set(state => ({ order: { ...state.order, service } })),
    setPlace: (place: Place) => set(state => ({ order: { ...state.order, place } })),
    setGeneration: (generation: Generation) => set( state => ({ order: { ...state.order, generation } }) ),
    setMotorization: (motorization: Motorization) => set( state => ({ order: { ...state.order, motorization } }) ),
    setBody: (body: Body) => set( state => ({ order: { ...state.order, body } }) ),
    setBrand: (brand: Brand) => set( state => ({ order: { ...state.order, brand } }) ),
    setGearbox: (gearbox: Gearbox) => set( state => ({ order: { ...state.order, gearbox } }) ),
    setGarage: (garage: Garage) => set( state => ({ order: { ...state.order, garage } }) ),
    setSlot: (slot: Slot) => set( state => ({ order: { ...state.order, slot } }) ),
    setIsLoading: (isListLoading: boolean) => set(state => ({ isLoading: isListLoading })),
    setPartnerConfig: (config: PartnerConfig) => set(state =>({ ...state.partner, partner: config })),
    setCurrentlyAvailableGenerations: (generations: Generation[]) => set(state => ({ filters: { ...state.filters, currentlyAvailableGenerations: generations } })),
    getFirstName: () => get().bookingContact.firstName,
    setFirstName: (firstName: string) => set(state => ({ bookingContact: { ...state.bookingContact, firstName } })),
    getLastName: () => get().bookingContact.lastName,
    setLastName: (lastName: string) => set(state => ({ bookingContact: { ...state.bookingContact, lastName } })),
    getMail: () => get().bookingContact.mail,
    setMail: (mail: string) => set(state => ({ bookingContact: { ...state.bookingContact, mail } })),
    getPhone: () => get().bookingContact.phone,
    setPhone: (phone: PhoneInput) => set(state => ({ bookingContact: { ...state.bookingContact, phone } })),
    getMileage: () => get().bookingContact.mileage,
    setMileage: (mileage: number) => set(state => ({ bookingContact: { ...state.bookingContact, mileage } })),
    getStreet: () => get().bookingContact.street,
    setStreet: (street: string) => set(state => ({ bookingContact: { ...state.bookingContact, street } })),
    getCity: () => get().bookingContact.city,
    setCity: (city: string) => set(state => ({ bookingContact: { ...state.bookingContact, city } })),
    getPostcode: () => get().bookingContact.postcode,
    setPostcode: (postcode: string) => set(state => ({ bookingContact: { ...state.bookingContact, postcode } })),
    getAddition: () => get().bookingContact.addition,
    setAddition: (addition: string) => set(state => ({ bookingContact: { ...state.bookingContact, addition } })),
    getNumberplate: () => get().bookingContact.numberplate,
    setNumberplate: (numberplate: NumberPlate) => set(state => ({ bookingContact: { ...state.bookingContact, numberplate } })),
    getMessage: () => get().bookingContact.message,
    setMessage: (message: string) => set(state => ({ bookingContact: { ...state.bookingContact, message } })),
    getAcceptedTos: () => get().bookingContact.acceptedTos,
    setAcceptedTos: (acceptedTos: boolean) => set(state => ({ bookingContact: { ...state.bookingContact, acceptedTos } })),
    getAcceptedMarketing: () => get().bookingContact.acceptedMarketing,
    setAcceptedMarketing: (acceptedMarketing: boolean) => set(state => ({ bookingContact: { ...state.bookingContact, acceptedMarketing } })),
    getAcceptedAdacMember: () => get().bookingContact.acceptedAdacMember,
    setAcceptedAdacMember: (acceptedAdacMember: boolean) => set(state => ({ bookingContact: { ...state.bookingContact, acceptedAdacMember } })),
}))

if (process.env.NODE_ENV === 'development') {
    import('simple-zustand-devtools').then(exports => {
        exports.mountStoreDevtool('Store', useStore)
    });
}

export default useStore
